<template>
  <SoDialog
    ref="reprocess-payment-review-record-modal"
    v-model="isOpen"
    :card-title="`You're About to Reprocess ${recordCount} Accounts`"
    data-testid="reprocess-payment-review-record-modal"
  >
    <SoForm @submit="reprocess">
      <p class="text-h6">
        If the balance is too high, you can choose to exclude balance from
        reprocess. To ignore large change threshold, retry with no limiter.
        Alternatively, you may proceed without any selection.
      </p>
      <SoCheckbox
        v-model="retryOptions.ignoreAccountBalance"
        data-testid="reprocess-payment-exclude-balance-checkbox"
        label="Exclude balance from reprocess"
        class="mt-4"
      />
      <SoCheckbox
        v-model="retryOptions.overrideIncreaseThreshold"
        label="Retry with no limiter"
      />
      <div class="mt-5 d-flex justify-end">
        <SoButton title="Cancel" large @click="isOpen = false" />
        <SoButton
          data-testid="reprocess-payment-submit-btn"
          :loading="isReprocessing"
          title="Reprocess"
          type="success"
          class="ml-3"
          submit
          large
        />
      </div>
    </SoForm>

    <SoAlert
      v-if="error"
      type="error"
      :title="error"
      class="mt-5"
      data-testid="reprocess-payment-review-record-modal-error"
    />
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import {
    PaymentReviewRecordFilters,
    RetryOptions,
  } from "@/services/operations/PaymentReviewService"
  import { PaymentReviewMutator } from "@/mutators/paymentReview/PaymentReviewMutator"
  import { PaymentReviewsRetryResponse } from "@/api/backofficeRest/generated/model"
  import SoForm from "@/components/formControls/SoForm.vue"
  import SoCheckbox from "@/components/formControls/SoCheckbox.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import { ReprocessMethod } from "@/views/operations/directDebitReviews/opsDirectDebitReviewConfig"
  import { errorHandler, errorReporter } from "@/services/ErrorService"

  export default defineComponent({
    components: {
      SoForm,
      SoCheckbox,
      SoButton,
      SoDialog,
      SoAlert,
    },
    props: {
      modelValue: {
        type: Boolean,
        required: true,
      },
      paymentReviewId: {
        type: Number,
        required: true,
      },
      reprocessMethod: {
        type: String as PropType<ReprocessMethod>,
        required: true,
      },
      recordIds: {
        type: Array as PropType<number[]>,
        default: () => [],
      },
      recordCount: {
        type: Number,
        default: 0,
      },
      filters: {
        type: Object as PropType<PaymentReviewRecordFilters>,
        default: () => ({}),
      },
    },
    emits: ["update:modelValue", "success"],
    data() {
      return {
        error: "",
        isReprocessing: false,
        retryOptions: {
          overrideIncreaseThreshold: false,
          ignoreAccountBalance: false,
        } as RetryOptions,
      }
    },
    computed: {
      isOpen: {
        get(): boolean {
          return this.modelValue
        },
        set(val: boolean) {
          this.$emit("update:modelValue", val)
        },
      },
    },
    methods: {
      async reprocess() {
        this.isReprocessing = true
        try {
          if (this.reprocessMethod === ReprocessMethod.ByIds) {
            await this.reprocessByIds()
          } else {
            await this.reprocessByFilters()
          }
          this.$emit("success")
          this.isOpen = false
        } catch (e) {
          this.error = errorHandler.userMessage(
            e,
            "Failed to reprocess records",
          )
          errorReporter.report(e)
          console.error(e)
        }

        this.isReprocessing = false
      },

      async reprocessByIds() {
        const { mutateAsync } = PaymentReviewMutator.retryRecordsByIds()
        const response = (await mutateAsync({
          ids: this.recordIds,
          ...this.retryOptions,
        })) as PaymentReviewsRetryResponse

        if (!response?.result) {
          throw new Error("Failed to reprocess selected records")
        }
      },

      async reprocessByFilters() {
        const { mutateAsync } = PaymentReviewMutator.retryRecordsByFilters()
        // `result` carries no documented contract here, so acceptance is the
        // gateway answering at all; a refusal arrives as a rejection.
        await mutateAsync({
          paymentReviewId: this.paymentReviewId,
          filters: this.filters,
          ...this.retryOptions,
        })
      },
    },
  })
</script>
